import { ArrowRight, Layers, MapPin, Search } from "lucide-react";
import useSWR from "swr";

import api from "../../../../lib/api";
import type {
  CourtComplexRow,
  DistrictCourtRow,
  DistrictRow,
  EstablishmentRow,
} from "./types";
import {
  BackButton,
  DataTable,
  MobileInfoRow,
  OrangeIconBox,
  RowActions,
  ScreenTitle,
  StatusBadge,
  rowClassName,
} from "./DistrictCourtUi";

interface DistrictCourtsProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onBack: () => void;
  onAdd: () => void;
  onOpen: (district: DistrictCourtRow) => void;
  onEdit: (district: DistrictCourtRow) => void;
  onDelete: (district: DistrictCourtRow) => void;
}

export const DistrictCourts = ({
  searchTerm,
  onSearchTermChange,
  onBack,
  onAdd,
  onOpen,
  onEdit,
  onDelete,
}: DistrictCourtsProps) => {
  const { data: rows = [], isLoading } = useSWR(
    "new-courts-district-court-rows",
    loadDistrictCourtRows,
  );
  const term = searchTerm.trim().toLowerCase();
  const districts = term
    ? rows.filter((district) =>
        [district.districtName, district.stateName, district.headquarter]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term)),
      )
    : rows;

  return (
    <div>
      <BackButton label="Back to Courts" onClick={onBack} />
      <ScreenTitle
        title="District Courts"
        count={`${districts.length} court${districts.length === 1 ? "" : "s"}`}
        subtitle="Click on a district court to manage its complexes and courtrooms"
        actionLabel="Add District Court"
        onAction={onAdd}
      />

      <div className="mb-5 flex gap-3">
        <div className="flex h-12 flex-1 items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white px-4 focus-within:border-[#f26422] focus-within:ring-2 focus-within:ring-orange-100">
          <Search className="h-5 w-5 text-slate-400" />
          <input
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            placeholder="Search by district, state, or court name..."
            className="h-full flex-1 bg-transparent outline-none"
          />
        </div>
      </div>

      <DataTable
        headers={[
          "District Court Name",
          "State",
          "District",
          "Headquarters",
          "Status",
          "Actions",
        ]}
        isLoading={isLoading}
        emptyLabel="Add more district courts to this list"
        emptyAction="Add District Court"
        onEmptyAction={onAdd}
        mobileCards={districts.map((district) => (
          <article
            key={district._id}
            className="rounded-xl border border-[#E8EDF5] bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <button
                type="button"
                onClick={() => onOpen(district)}
                className="flex min-w-0 items-start gap-3 text-left"
              >
                <OrangeIconBox small>
                  <Layers className="h-5 w-5" />
                </OrangeIconBox>
                <span className="min-w-0">
                  <span className="block font-semibold text-[#f26422]">
                    {district.districtName}
                    <ArrowRight className="ml-1 inline h-4 w-4 text-[#f9a07a]" />
                  </span>
                  <span className="text-sm text-slate-400">
                    {district.complexCount} complexes ·{" "}
                    {district.courtRoomCount} courtrooms
                  </span>
                </span>
              </button>
              <RowActions
                onEdit={() => onEdit(district)}
                onDelete={() => onDelete(district)}
              />
            </div>
            <div className="mt-4 space-y-3">
              <MobileInfoRow label="State" value={district.stateName || "N/A"} />
              <MobileInfoRow
                label="District"
                value={district.districtName || "N/A"}
              />
              <MobileInfoRow
                label="Headquarters"
                value={
                  <span className="inline-flex items-center justify-end gap-1.5">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    {district.headquarter || district.capitalCity || "N/A"}
                  </span>
                }
              />
              <MobileInfoRow
                label="Status"
                value={<StatusBadge status={district.status || "Active"} />}
              />
            </div>
          </article>
        ))}
      >
        {districts.map((district) => (
          <tr key={district._id} className={rowClassName}>
            <td className="px-6 py-5">
              <button
                type="button"
                onClick={() => onOpen(district)}
                className="flex items-center gap-3 text-left"
              >
                <OrangeIconBox small>
                  <Layers className="h-5 w-5" />
                </OrangeIconBox>
                <span>
                  <span className="block font-semibold text-[#f26422]">
                    {district.districtName}
                    <ArrowRight className="ml-1 inline h-4 w-4 text-[#f9a07a]" />
                  </span>
                  <span className="text-sm text-slate-400">
                    {district.complexCount} complexes ·{" "}
                    {district.courtRoomCount} courtrooms
                  </span>
                </span>
              </button>
            </td>
            <td className="px-6 py-5 text-slate-700">{district.stateName}</td>
            <td className="px-6 py-5 text-slate-700">
              {district.districtName || "N/A"}
            </td>
            <td className="px-6 py-5 text-slate-700">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-slate-400" />
                {district.headquarter || district.capitalCity || "N/A"}
              </span>
            </td>
            <td className="px-6 py-5">
              <StatusBadge status={district.status || "Active"} />
            </td>
            <td className="px-6 py-5">
              <RowActions
                onEdit={() => onEdit(district)}
                onDelete={() => onDelete(district)}
              />
            </td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
};

const loadDistrictCourtRows = async (): Promise<DistrictCourtRow[]> => {
  const districtRows = await fetchRows<DistrictRow>(
    "/api/court/get-districts?page=1&limit=1000",
  );

  return Promise.all(
    districtRows.map(async (district) => {
      const complexes = await fetchRows<CourtComplexRow>(
        `/api/court/get-complexes/${district._id}`,
      );
      const courtRoomCounts = await Promise.all(
        complexes.map(async (complex) => {
          if (!complex.courtEstablishment) {
            const rooms = await fetchRows(
              `/api/court/get-courtrooms/${complex._id}`,
            );
            return rooms.length;
          }

          const establishments = await fetchRows<EstablishmentRow>(
            `/api/court/get-establishments/${complex._id}`,
          );
          const roomGroups = await Promise.all(
            establishments.map((establishment) =>
              fetchRows(`/api/court/get-courtrooms/${establishment._id}`),
            ),
          );

          return roomGroups.reduce((total, rooms) => total + rooms.length, 0);
        }),
      );

      return {
        ...district,
        stateName: district.stateName || "",
        complexCount: complexes.length,
        courtRoomCount: courtRoomCounts.reduce(
          (total, count) => total + count,
          0,
        ),
      };
    }),
  );
};

const fetchRows = async <T,>(url: string): Promise<T[]> => {
  try {
    const response = await api.get(url);
    const data = response?.data;
    if (data?.status === false) return [];
    return Array.isArray(data?.data) ? data.data : [];
  } catch {
    return [];
  }
};
